import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MediaProvider } from './context/MediaContext';
import { AdminProvider } from './context/AdminContext';
import { Layout } from './components/Layout';
import { AdminLayout } from './components/AdminLayout';
import { RoleGuard } from './components/RoleGuard';

// Loading Component
const PageLoader = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
  </div>
);

// Pages
const Home = React.lazy(() => import('./pages/Home').then(m => ({ default: m.Home })));
const ArticleView = React.lazy(() => import('./pages/ArticleView').then(m => ({ default: m.ArticleView })));
const CategoryPage = React.lazy(() => import('./pages/CategoryPage').then(m => ({ default: m.CategoryPage })));
const AudioHub = React.lazy(() => import('./pages/AudioHub').then(m => ({ default: m.AudioHub })));
const VideoHub = React.lazy(() => import('./pages/VideoHub').then(m => ({ default: m.VideoHub })));
const ShowPage = React.lazy(() => import('./pages/ShowPage').then(m => ({ default: m.ShowPage })));
const EpisodeView = React.lazy(() => import('./pages/EpisodeView').then(m => ({ default: m.EpisodeView })));
const CompanyPage = React.lazy(() => import('./pages/CompanyPage').then(m => ({ default: m.CompanyPage })));
const CompanyAbout = React.lazy(() => import('./pages/CompanyAbout').then(m => ({ default: m.CompanyAbout })));
const ContactPage = React.lazy(() => import('./pages/ContactPage').then(m => ({ default: m.ContactPage })));
const AboutUs = React.lazy(() => import('./pages/AboutUs').then(m => ({ default: m.AboutUs })));

// Admin Pages
const AdminLogin = React.lazy(() => import('./pages/admin/AdminLogin').then(m => ({ default: m.AdminLogin })));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const ContentEditor = React.lazy(() => import('./pages/admin/ContentEditor').then(m => ({ default: m.ContentEditor })));
const ContentLibrary = React.lazy(() => import('./pages/admin/ContentLibrary').then(m => ({ default: m.ContentLibrary })));
const MediaStudio = React.lazy(() => import('./pages/admin/MediaStudio').then(m => ({ default: m.MediaStudio })));
const StaffDirectory = React.lazy(() => import('./pages/admin/StaffDirectory').then(m => ({ default: m.StaffDirectory })));
const ReviewQueue = React.lazy(() => import('./pages/admin/ReviewQueue').then(m => ({ default: m.ReviewQueue })));
const AuditLogViewer = React.lazy(() => import('./pages/admin/AuditLogViewer').then(m => ({ default: m.AuditLogViewer })));
const PortalSettings = React.lazy(() => import('./pages/admin/PortalSettings').then(m => ({ default: m.PortalSettings })));

const NewsHub = React.lazy(() => import('./pages/NewsHub').then(m => ({ default: m.NewsHub })));

const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Layout><Home /></Layout>} />
        <Route path="/news" element={<Layout><NewsHub /></Layout>} />
        <Route path="/news/:category" element={<Layout><CategoryPage /></Layout>} />
        <Route path="/news/:category/:id" element={<Layout><ArticleView /></Layout>} />
        <Route path="/audio" element={<Layout><AudioHub /></Layout>} />
        <Route path="/video" element={<Layout><VideoHub /></Layout>} />
        <Route path="/audio/show/:id" element={<Layout><ShowPage /></Layout>} />
        <Route path="/audio/episode/:id" element={<Layout><EpisodeView /></Layout>} />
        <Route path="/video/show/:id" element={<Layout><ShowPage /></Layout>} />
        <Route path="/video/episode/:id" element={<Layout><EpisodeView /></Layout>} />
        <Route path="/company/about" element={<Layout><CompanyAbout /></Layout>} />
        <Route path="/company/contact" element={<Layout><ContactPage /></Layout>} />
        <Route path="/about" element={<Layout><AboutUs /></Layout>} />
        <Route path="/company/:slug" element={<Layout><CompanyPage /></Layout>} />

        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
        <Route path="/admin/content" element={<AdminLayout><ContentLibrary /></AdminLayout>} />
        <Route path="/admin/review" element={<AdminLayout><ReviewQueue /></AdminLayout>} />
        <Route path="/admin/content/new" element={<AdminLayout><ContentEditor /></AdminLayout>} />
        <Route path="/admin/content/edit/:id" element={<AdminLayout><ContentEditor /></AdminLayout>} />

        {/* Production & System Admin */}
        <Route path="/admin/audio" element={<AdminLayout><MediaStudio /></AdminLayout>} />
        <Route path="/admin/video" element={<AdminLayout><MediaStudio /></AdminLayout>} />

        <Route path="/admin/users" element={
          <RoleGuard allowedRoles={['admin', 'root']}>
            <AdminLayout><StaffDirectory /></AdminLayout>
          </RoleGuard>
        } />

        <Route path="/admin/logs" element={
          <RoleGuard allowedRoles={['admin', 'root']}>
            <AdminLayout><AuditLogViewer /></AdminLayout>
          </RoleGuard>
        } />

        <Route path="/admin/settings" element={
          <RoleGuard allowedRoles={['admin', 'root']}>
            <AdminLayout><PortalSettings /></AdminLayout>
          </RoleGuard>
        } />

        <Route path="*" element={<Layout><div className="text-center py-20 text-gray-500">404 Not Found</div></Layout>} />
      </Routes>
    </Suspense>
  );
};

const App: React.FC = () => {
  return (
    <AdminProvider>
      <MediaProvider>
        <Router>
          <AppRoutes />
        </Router>
      </MediaProvider>
    </AdminProvider>
  );
};

export default App;